import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { tableCellClasses, tableRowClasses } from '@mui/material';
import { TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { Table, TableCellText, TablePrimaryCell } from '../../../../components/Metrics/MetricTable';
import { Metric } from '../../../../types/Analytics/Metric';
import { setupMetricChange } from '../../../../components/Metrics/MetricCard';
import Column from '../../../../components/Column';
import MetricChangeLabel from '../../../../components/Metrics/MetricChangeLabel';
import HatUtils from '../../../../utils/HatUtils';
import Utils from '../../../../components/Utils';

import { QueryRegistryKey } from '../../../../services/trialfire/hooks/QueryRegistry';
import { useHatDashboardPageContext } from '../../../../hooks/Pages/HatDashboardPage';

interface TableRow {
    conversionName: string;
    totalConversions: Metric;
    totalAdInfluencedConversion: Metric;
    percentAdInfluenced: Metric;
}

interface TableColumn {
    label: string;
    totalKey: QueryRegistryKey;
    totalAdInfluencedKey: QueryRegistryKey;
}

interface TableState {
    rows: TableRow[];
    setRows: Dispatch<SetStateAction<TableRow[]>>;
}

interface TableProps {
    rows: TableRow[];
}

const columns: TableColumn[] = [
    {
        label: 'SRP Views',
        totalKey: 'TOTAL_CONVERSIONS_SRP_VIEWS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS',
    },
    {
        label: 'VDP Views',
        totalKey: 'TOTAL_CONVERSIONS_VDP_VIEWS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS',
    },
    {
        label: 'Service Page Views',
        totalKey: 'TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS',
    },
    {
        label: 'Specials Page Views',
        totalKey: 'TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS',
    },
    {
        label: 'Form Submissions',
        totalKey: 'TOTAL_CONVERSIONS_IDENTIFIED_VISITORS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS',
    },
    {
        label: 'Click to Calls',
        totalKey: 'TOTAL_CONVERSIONS_CLICK_TO_CALLS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS',
    },
    {
        label: 'Total Hours and Directions Views',
        totalKey: 'TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
    },
];

const useHatConversionTrafficTable = (): TableState => {
    const { queries, previousQueries } = useHatDashboardPageContext();
    const [rows, setRows] = useState<TableRow[]>([]);

    const buildMetric = useCallback(
        (key: QueryRegistryKey, previousKey: QueryRegistryKey, format: Metric['format'] = 'number'): Metric =>
            setupMetricChange({
                format,
                value: queries[key]?.data?.data ?? 0,
                previousValue: previousQueries[previousKey]?.data?.data ?? 0,
                isLoading:
                    (queries[key]?.isLoading && queries[key]?.isFetching) ||
                    (previousQueries[previousKey]?.isLoading && previousQueries[previousKey]?.isFetching),
            }),
        [queries, previousQueries]
    );

    useEffect(() => {
        const _rows = columns.map(({ label, totalKey, totalAdInfluencedKey }) => {
            const totalConversions = buildMetric(totalKey, totalKey);
            const totalAdInfluencedConversion = buildMetric(totalAdInfluencedKey, totalAdInfluencedKey);

            const percentAdInfluenced = setupMetricChange({
                format: 'number',
                value:
                    totalConversions.value > 0
                        ? Math.round(
                              (totalAdInfluencedConversion.value /
                                  (totalConversions.value + totalAdInfluencedConversion.value)) *
                                  100
                          )
                        : 0,
                previousValue:
                    totalConversions.previousValue &&
                    totalAdInfluencedConversion.previousValue &&
                    totalConversions.previousValue > 0
                        ? Math.round(
                              (totalAdInfluencedConversion.previousValue /
                                  (totalConversions.previousValue + totalAdInfluencedConversion.previousValue)) *
                                  100
                          )
                        : 0,
                isLoading: totalConversions.isLoading || totalAdInfluencedConversion.isLoading,
            });

            return {
                conversionName: label,
                totalConversions,
                totalAdInfluencedConversion,
                percentAdInfluenced,
            };
        });

        // Sort the rows in descending order
        _rows.sort((a: TableRow, b: TableRow) => b.totalConversions.value - a.totalConversions.value);

        const grandTotalConversions: Metric = setupMetricChange({
            format: 'number',
            value: _rows.reduce((sum, _row) => sum + _row.totalConversions.value, 0),
            previousValue: _rows.reduce((sum, _row) => sum + (_row.totalConversions.previousValue || 0), 0),
        });
        const grandTotalAdInfluencedConversion: Metric = setupMetricChange({
            format: 'number',
            value: _rows.reduce((sum, _row) => sum + _row.totalAdInfluencedConversion.value, 0),
            previousValue: _rows.reduce((sum, _row) => sum + (_row.totalAdInfluencedConversion.previousValue || 0), 0),
        });

        _rows.push({
            conversionName: 'Total',
            totalConversions: grandTotalConversions,
            totalAdInfluencedConversion: grandTotalAdInfluencedConversion,
            percentAdInfluenced: setupMetricChange({
                format: 'number',
                value:
                    grandTotalConversions.value > 0
                        ? Math.round(
                              (grandTotalAdInfluencedConversion.value /
                                  (grandTotalConversions.value + grandTotalAdInfluencedConversion.value)) *
                                  100
                          )
                        : 0,
                previousValue:
                    grandTotalConversions.previousValue &&
                    grandTotalAdInfluencedConversion.previousValue &&
                    grandTotalConversions.previousValue > 0
                        ? Math.round(
                              (grandTotalAdInfluencedConversion.previousValue /
                                  (grandTotalConversions.previousValue +
                                      grandTotalAdInfluencedConversion.previousValue)) *
                                  100
                          )
                        : 0,
                isLoading: true,
            }),
        });

        setRows(_rows);
    }, [buildMetric]);

    return { rows, setRows };
};

const HatConversionTrafficTable: FC<TableProps> = ({ rows }): React.JSX.Element => {
    const { hasHatCriteriaPreviousDateRange } = useHatDashboardPageContext();
    return (
        <TableContainer
            sx={{
                borderCollapse: 'inherit',
                borderColor: (theme) => theme.palette.grey[300],
                borderStyle: 'solid',
                borderWidth: 1,
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            <Table
                sx={{
                    [`& .${tableRowClasses.root} .${tableCellClasses.root}:nth-of-type(1)`]: {
                        textAlign: 'left',
                        width: 'auto',
                    },
                    [`& .${tableRowClasses.root} .${tableCellClasses.root}:nth-of-type(4)`]: {
                        width: '12vw',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Action</TableCell>
                        <TableCell>All</TableCell>
                        <TableCell>
                            <TableCellText tooltip="Actions that can be directly attributed to our campaigns, and are reclassified from other channels.">
                                Attributed Actions
                            </TableCellText>
                        </TableCell>
                        <TableCell>% Attributed Actions</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map(
                        (
                            { conversionName, totalConversions, totalAdInfluencedConversion, percentAdInfluenced },
                            index
                        ) => (
                            <TableRow key={index}>
                                <TablePrimaryCell>{conversionName}</TablePrimaryCell>
                                {[totalConversions, totalAdInfluencedConversion, percentAdInfluenced].map(
                                    (metric, i) => (
                                        <TableCell key={i}>
                                            <Column gap={0} alignItems="flex-end">
                                                {Utils.formatValue(metric.value, i === 2 ? 'percent' : undefined)}

                                                {hasHatCriteriaPreviousDateRange && (
                                                    <MetricChangeLabel
                                                        variant="body2"
                                                        label={HatUtils.formatPercent(metric.change)}
                                                        direction={
                                                            metric.change && metric.change !== 0
                                                                ? metric.change > 0
                                                                    ? 'up'
                                                                    : 'down'
                                                                : 'center'
                                                        }
                                                    />
                                                )}
                                            </Column>
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        )
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export type { TableState as UseHatConversionTrafficTableState, TableProps as HatConversionTrafficTableProps };
export { useHatConversionTrafficTable };
export default HatConversionTrafficTable;
